import { useShallow } from 'zustand/react/shallow';
import { FeatureFlagEnum } from '../featureflag/feature-flag.enum';
import { CurrencyType } from '../modules/brand/currency/currencyType.enum';
import { Locales } from '../modules/translation/messages';
import useSystemParametersStore from '../stores/SystemParameters/useSystemParameters.store';
import useEnvironment from './useEnvironments';

export const useFeatureFlag = (isOfflineMode = false) => {
  const { isDevelopment } = useEnvironment();

  const enableFeatureFlagsInDevMode =
    isDevelopment &&
    import.meta.env.VITE_DISABLE_AUTO_ENABLE_OF_FEATURE_FLAGS_IN_DEV_MODE !== 'true';

  const getFeatureFlag = (flag: FeatureFlagEnum, defaultValue = false) =>
    enableFeatureFlagsInDevMode ||
    useSystemParametersStore.getState().getSystemParameterActive(flag, defaultValue);

  const featureFlags = useSystemParametersStore(
    useShallow((state) => {
      const getFlag = (flag: FeatureFlagEnum, defaultValue = false) =>
        enableFeatureFlagsInDevMode || state.getSystemParameterActive(flag, defaultValue);

      return {
        isEnableNps: getFlag(FeatureFlagEnum.ENABLE_NPS),
        isEnableSatisfactionSurvey: getFlag(FeatureFlagEnum.ENABLE_SATISFACTION_SURVEY),
        isEnablePos: getFlag(FeatureFlagEnum.ENABLE_STORE_POINT_OF_SALE_MODAL),
        isEnableChat: getFlag(FeatureFlagEnum.ENABLE_CHAT),
        isEnableChatInCases: getFlag(FeatureFlagEnum.ENABLE_CHAT_IN_CASES),
        isEnableChatVideoCall: getFlag(FeatureFlagEnum.ENABLE_CHAT_VIDEO_CALL),
        isEnableOrderBookSalesOpportunityConversions: getFlag(
          FeatureFlagEnum.ENABLE_ORDER_BOOK_SALES_OPPORTUNITY_CONVERSIONS,
          true,
        ),
        isEnableOrderBookSalesOpportunity: getFlag(
          FeatureFlagEnum.ENABLE_ORDER_BOOK_SALES_OPPORTUNITY,
          true,
        ),
        isEnableShareSuggestedOrder: getFlag(FeatureFlagEnum.ENABLE_SHARE_SUGGESTED_ORDER, true),
        isEnableSuggestedOrder: getFlag(FeatureFlagEnum.ENABLE_SUGGESTED_ORDER_V2, true),
        isEnableCases: getFlag(FeatureFlagEnum.ENABLE_CASES, true),
        isEnableInvoices: getFlag(FeatureFlagEnum.ENABLE_INVOICES, true),
        sellerCanCreateOrChangeSuggestedOrder: getFlag(
          FeatureFlagEnum.ENABLE_SELLER_TO_CREATE_OR_CHANGE_SUGGESTED_ORDER,
          true,
        ),
        brandCurrencyType: state.getSystemParameterValue<CurrencyType>(
          FeatureFlagEnum.CURRENCY_TYPE,
          CurrencyType.BRL,
        ) as CurrencyType,
        brandCurrencyLocale: state.getSystemParameterValue<Locales>(
          FeatureFlagEnum.BRAND_CURRENCY_LOCALE,
          Locales.PORTUGUESE_BR,
        ) as Locales,
        isVoucherEnabled: getFlag(FeatureFlagEnum.ENABLE_BUSINESS_POLICY_VOUCHER),
        isTeceoOfflineEnabled: getFlag(FeatureFlagEnum.ENABLE_TECEO_OFFLINE),
        isUpdateOrderOnApproveOrderMassActionEnabled: state.getSystemParameterActive(
          FeatureFlagEnum.ENABLE_UPDATE_ORDER_ON_APPROVE_ORDER_MASS_ACTION,
          false,
        ),
        isEnableAdminChangeSplitSettings: state.getSystemParameterActive(
          FeatureFlagEnum.ENABLE_ADMIN_CHANGE_SPLIT_SETTINGS,
          false,
        ),
        maxItemsToPasteInProductDetails: isOfflineMode
          ? Number.MAX_SAFE_INTEGER
          : (state.getGlobalSystemParameterValue<number>(
              FeatureFlagEnum.MAX_ITEMS_TO_PASTE_IN_PRODUCT_DETAILS,
              20,
            ) as number),
        isAccountsReceivableEnabled: state.getSystemParameterActive(
          FeatureFlagEnum.ENABLE_ACCOUNTS_RECEIVABLE,
          false,
        ),
        isEnableHideWholeSaleRetailProductSettings: getFlag(
          FeatureFlagEnum.ENABLE_HIDE_WHOLESALE_RETAIL_PRODUCTS_SETTINGS,
          true,
        ),
        isProductMatchEnabled: getFlag(FeatureFlagEnum.ENABLE_PRODUCT_MATCH),
        isSalesOpportunityEnabled: getFlag(FeatureFlagEnum.ENABLE_SALES_OPPORTUNITY, true),
        isEnableRachelChat: getFlag(FeatureFlagEnum.ENABLE_RACHEL_CHAT),
        isEnableLightdashReports: getFlag(FeatureFlagEnum.ENABLE_LIGHTDASH_REPORTS),
        isLocalsEnabled: getFlag(FeatureFlagEnum.ENABLE_LOCALS),
        isSkuWeightAndDimensionsEnabled: getFlag(FeatureFlagEnum.ENABLE_SKU_WEIGHT_AND_DIMENSIONS),
      };
    }),
  );

  return {
    getFeatureFlag,
    ...featureFlags,
  };
};
